import { useMemo } from 'react';

import { ZoomComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { ViewProps } from './image.constants';
import {
  ImageViewWrapper,
  ImageContainer,
  ImageFillWrapper,
  ImageFigure,
} from './image.style';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import type { GetActivitiesSectionsQuery } from '../../lesson-builder.graphql.generated';
import { computePaddingTop } from '../shared/component-height/component-dimension.utils';
import { getLargestImage } from '../shared/logic/get-largest-image';
import {
  ComponentHeightWrapper,
  ComponentInnerWrapper,
} from '../shared/component-height/component-dimensions.style';
import { ComponentCaption } from '../shared/component-caption/component-caption';
import { ComponentImage } from '../component-image';
import type { Pluck } from 'lib/gql/pluck';

export const ImageViewFit = ({
  imageUrls,
  altText,
}: {
  imageUrls: Pluck<
    Extract<
      Pluck<GetActivitiesSectionsQuery, 'getActivitiesSections.components'>,
      { __typename: 'ActivitiesImageComponent' }
    >,
    'properties.imageUrls'
  >[];
  altText?: string | null;
}) => {
  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl: getLargestImage(imageUrls),
  });
  return (
    <ImageViewWrapper ref={setContainerElement}>
      <ComponentImage
        imageDimensions={imageDimensions}
        images={imageUrls || []}
        altText={altText}
      />
    </ImageViewWrapper>
  );
};

export const ImageViewFill = ({
  imageUrls,
  altText,
}: {
  imageUrls: Pluck<
    Extract<
      Pluck<GetActivitiesSectionsQuery, 'getActivitiesSections.components'>,
      { __typename: 'ActivitiesImageComponent' }
    >,
    'properties.imageUrls'
  >[];
  altText?: string | null;
}) => {
  return (
    <ImageFillWrapper>
      <ComponentImage images={imageUrls || []} altText={altText} />
    </ImageFillWrapper>
  );
};

const ImageView = ({
  id,
  properties: {
    showCaptionOnLesson,
    imageUrls,
    dimensionData,
    cropData,
    caption,
    altText,
  },
  stimulusResourceProperties,
  openModal,
}: ViewProps) => {
  const isFit = !cropData;
  const imageAltText = stimulusResourceProperties?.altText || altText;
  const hasImages = useMemo(() => {
    return (
      (imageUrls?.length ?? 0) > 0 ||
      !!stimulusResourceProperties?.image?.original
    );
  }, [imageUrls, stimulusResourceProperties?.image?.original]);
  return (
    <ImageFigure>
      <ComponentHeightWrapper
        style={{
          paddingTop: `${computePaddingTop(dimensionData)}%`,
        }}
      >
        <ComponentInnerWrapper>
          <ImageContainer $fullBorderRadius={!showCaptionOnLesson}>
            {openModal && hasImages && (
              <ZoomComponentTrigger
                ariaLabel={imageAltText || 'image'}
                openModal={openModal}
              />
            )}
            {isFit ? (
              <ImageViewFit
                altText={imageAltText}
                imageUrls={imageUrls || []}
              />
            ) : (
              <ImageViewFill
                altText={imageAltText}
                imageUrls={imageUrls || []}
              />
            )}
          </ImageContainer>
          <ComponentCaption
            id={id}
            caption={caption}
            showCaption={showCaptionOnLesson}
          />
        </ComponentInnerWrapper>
      </ComponentHeightWrapper>
    </ImageFigure>
  );
};

export default ImageView;
